import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';

const VALUE_LIMIT = 100;

/**
 * Loads the distinct values of a field (most-active first) to populate the page
 * filter row's value picker. Transient; dedupes by field, clears stale options
 * when the field changes, and discards superseded responses so the picker never
 * shows another field's values.
 */
@injectable()
export class FilterValuesViewModel extends BaseViewModel {
    field: AnalyticsField | null = null;
    options: string[] = [];
    loading = false;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async loadFor(field: AnalyticsField): Promise<void> {
        if (this.field === field && (this.options.length > 0 || this.loading)) return;
        this.field = field;
        this.options = [];
        this.loading = true;
        this.notifyChange();
        try {
            const result = await this.gateway.aggregate({
                source: 'interactions',
                groupBy: [field],
                metrics: ['turns'],
                orderBy: { metric: 'turns', direction: 'desc' },
                limit: VALUE_LIMIT,
            });
            if (this.field !== field) return;
            this.options = result.rows.map((row) => row.keys[field]).filter((value): value is string => value !== undefined && value.length > 0);
        } catch {
            if (this.field !== field) return;
            this.options = [];
        } finally {
            if (this.field === field) {
                this.loading = false;
                this.notifyChange();
            }
        }
    }
}
